import * as THREE from 'three';
import {getCarcaseTextures} from './advancedShelf';
import {PreviewFormValues} from 'components/customer/Preview3D/types';
import {addDragEffectOnClick} from 'components/customer/Preview3D/helpers';
import {
    applyCarcaseMaterial,
    applyWallFlushOutlineFix,
    attachWireframeToggleEvent,
    createLineSegments,
} from 'components/customer/Preview3DCommon/shelves/common';

export const generateUShapeMesh = ({
    width,
    depth,
    cutoutWidth,
    cutoutHeight,
    radius,
    carcaseTexture,
    carcaseEdgeTexture,
    complexRadii,
    sides,
    customCutoutHeight,
    carcaseThickness,
    horizontalGrainCarc,
    showTexture,
}: {
    width: number;
    depth: number;
    cutoutWidth: number;
    cutoutHeight: number;
    radius: number;
    carcaseTexture: THREE.Texture;
    carcaseEdgeTexture: THREE.Texture;
    complexRadii?: {
        interiorRadius: number;
        exteriorRadius: number;
    };
    sides?: {
        left: number;
        right: number;
    };
    customCutoutHeight?: number;
    carcaseThickness: number;
    horizontalGrainCarc?: boolean;
    showTexture?: boolean;
}) => {
    const shape = new THREE.Shape();
    const exteriorRadius = complexRadii ? complexRadii.exteriorRadius : radius;
    const interiorRadius = complexRadii ? complexRadii.interiorRadius : radius;

    const leftLength = sides ? sides.left : (width - cutoutWidth) / 2;
    const rightLength = sides ? width - sides.right : (width + cutoutWidth) / 2;

    const computedCutoutHeight = customCutoutHeight
        ? customCutoutHeight
        : cutoutHeight;

    // Outer shape with rounded corners and open bottom, starting from (0,0)
    shape.moveTo(0, 0);
    shape.absarc(width, 0, 0, Math.PI * 1.5, 0, false);
    shape.lineTo(width, depth);
    shape.lineTo(0, depth);
    shape.lineTo(0, 0);

    // Draw the cutout using lines and arcs
    shape.lineTo(leftLength - exteriorRadius, 0);
    shape.absarc(
        leftLength - exteriorRadius,
        exteriorRadius,
        exteriorRadius,
        Math.PI * 1.5,
        0,
        false
    );
    shape.lineTo(leftLength, computedCutoutHeight - interiorRadius);
    shape.absarc(
        leftLength + interiorRadius,
        computedCutoutHeight - interiorRadius,
        interiorRadius,
        Math.PI,
        Math.PI * 0.5,
        true
    );
    shape.lineTo(rightLength - interiorRadius, computedCutoutHeight);
    shape.absarc(
        rightLength - interiorRadius,
        computedCutoutHeight - interiorRadius,
        interiorRadius,
        Math.PI * 0.5,
        0,
        true
    );
    shape.lineTo(rightLength, computedCutoutHeight - interiorRadius);
    shape.lineTo(rightLength, exteriorRadius);
    shape.absarc(
        rightLength + exteriorRadius,
        exteriorRadius,
        exteriorRadius,
        Math.PI,
        Math.PI * 1.5,
        false
    );

    const extrudeSettings = {
        steps: 1,
        depth: carcaseThickness,
        bevelEnabled: false,
    };

    const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);
    const materials = applyCarcaseMaterial({
        geometry,
        texture: carcaseTexture,
        edgeTexture: carcaseEdgeTexture,
        useSubstrateEdge: false,
        flipGrain: true,
        hasHorizontalGrain: Boolean(horizontalGrainCarc),
        showTexture,
    });

    return new THREE.Mesh(geometry, materials);
};

export const renderSingleUShapedPanel = (values: PreviewFormValues) => {
    const carcaseThickness = values.cabinet_carc_thickness;

    const {carcaseTexture, carcaseEdgeTexture} = getCarcaseTextures(values);

    const radius = 50;
    const width = Number(values.cabinet_panel_width);
    const width1 = Number(values.cabinet_width1);
    const depth = Number(values.cabinet_panel_length);
    const length1 = Number(values.cabinet_length1);
    const length2 = Number(values.cabinet_length2);
    const cutoutWidth = depth - (length1 + length2);
    const cutoutHeight = depth * 0.5;

    const mesh = generateUShapeMesh({
        width: depth,
        depth: width,
        cutoutWidth,
        cutoutHeight,
        radius,
        carcaseTexture,
        carcaseEdgeTexture,
        complexRadii: {
            exteriorRadius: Number(values.cabinet_ext_radius),
            interiorRadius: Number(values.cabinet_int_radius),
        },
        sides: {
            left: length1,
            right: length2,
        },
        customCutoutHeight: width1,
        carcaseThickness,
    });

    mesh.rotation.x = Math.PI;
    mesh.position.setZ(carcaseThickness);
    mesh.scale.y = -1;

    // U-shape panels sit flush against the wall in the RoomPlanner. Push
    // the panel face slightly back in the depth buffer (polygonOffset) so
    // its outline beats the coplanar wall without disabling depth testing
    // on the line — that avoids the x-ray look.
    applyWallFlushOutlineFix(mesh);
    const lineSegments = createLineSegments(mesh);

    // attach userdata drag state events
    addDragEffectOnClick(
        mesh as unknown as THREE.Mesh<
            THREE.BoxGeometry,
            THREE.MeshStandardMaterial | THREE.MeshStandardMaterial[]
        >,
        lineSegments.material
    );
    // attach actual events
    attachWireframeToggleEvent(mesh);
    mesh.add(lineSegments);

    return mesh;
};
